import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// vAudit package stickers are 1D barcodes, so the scanner must decode linear formats as well
// as QR. Keep the list tight — every extra format costs decode time per frame.
const SUPPORTED_FORMATS: Html5QrcodeSupportedFormats[] = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.QR_CODE,
];

// html5-qrcode rejects a qrbox smaller than 50px on either side.
const MIN_BOX_EDGE = 50;

// A remount (restart, dialog close) must not start a new instance on the same element while the
// previous one is still tearing down its <video> — wait for the outstanding stop first.
const pendingStops = new Map<string, Promise<void>>();

export interface IQrScannerProps {
  /** DOM id of the element html5-qrcode renders into — must be unique on the page. */
  elementId?: string;
  /** Called once per scan with the decoded text; the camera is stopped before this fires. */
  onDecode: (text: string) => void;
  /** Called when the camera cannot start (no camera, permission denied, ...). */
  onError?: (message: string) => void;
  /** Bump to restart the camera after a decode (e.g. "Scan another"). */
  restartKey?: number;
  maxWidth?: number;
}

export const QrScanner = ({
  elementId = 'qr-scanner-reader',
  onDecode,
  onError,
  restartKey = 0,
  maxWidth = 460,
}: IQrScannerProps) => {
  const [cameraLoading, setCameraLoading] = useState(false);
  const [resizeKey, setResizeKey] = useState(0);
  const instanceRef = useRef<Html5Qrcode | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Keep the latest callbacks without restarting the camera when the parent re-renders.
  const onDecodeRef = useRef(onDecode);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onDecodeRef.current = onDecode;
    onErrorRef.current = onError;
  }, [onDecode, onError]);

  const stopCamera = useCallback(async () => {
    const instance = instanceRef.current;
    if (instance && instance.isScanning) {
      try {
        await instance.stop();
      } catch {
        // already stopped/stopping — nothing to do
      }
    }
  }, []);

  // html5-qrcode sizes the video and its scan-box overlay once, at start. When the reader's width
  // changes (phone rotated, drawer toggled, window resized) restart it so the box re-fits.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return undefined;
    let lastWidth = Math.round(wrapper.clientWidth);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (Math.abs(width - lastWidth) < 2) return;
      lastWidth = width;
      // Wait for the resize to settle — a drag or rotation fires many events.
      clearTimeout(timer);
      timer = setTimeout(() => setResizeKey((k) => k + 1), 300);
    });
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCameraLoading(true);

    const startCamera = async () => {
      try {
        await pendingStops.get(elementId);
        if (cancelled) return;
        const cameras = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!cameras || cameras.length === 0) {
          throw new Error('No camera found');
        }

        const instance = new Html5Qrcode(elementId, {
          verbose: false,
          formatsToSupport: SUPPORTED_FORMATS,
          // Native BarcodeDetector (Chrome/Android) is far better at 1D than the zxing fallback.
          useBarCodeDetectorIfSupported: true,
        });
        instanceRef.current = instance;

        await instance.start(
          // html5-qrcode only accepts a plain facingMode string or { exact: ... } here. A bare
          // string is a soft preference, so laptops without a rear camera still fall back.
          { facingMode: 'environment' },
          {
            fps: 10,
            // Wide, short box suited to 1D stickers; QR codes still fit inside it.
            qrbox: (viewfinderWidth: number, viewfinderHeight: number) => ({
              width: Math.max(MIN_BOX_EDGE, Math.floor(viewfinderWidth * 0.9)),
              height: Math.max(MIN_BOX_EDGE, Math.floor(viewfinderHeight * 0.4)),
            }),
          },
          async (decodedText) => {
            await stopCamera();
            onDecodeRef.current(decodedText);
          },
          () => {
            // Fires continuously while no code is in frame — not an error, ignore.
          },
        );

        // Unmounted/restarted while start() was pending — release the camera.
        if (cancelled) {
          await stopCamera();
        }
      } catch (e: any) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('QrScanner camera init failed:', e?.name, e?.message, e);
        onErrorRef.current?.(
          e?.name
            ? `${e.name}: ${e.message ?? 'Camera not available'} — enter the code manually.`
            : (e?.message ?? 'Camera not available — enter the code manually.'),
        );
      } finally {
        if (!cancelled) setCameraLoading(false);
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      pendingStops.set(elementId, stopCamera());
    };
  }, [elementId, restartKey, resizeKey, stopCamera]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth,
        mx: 'auto',
        minHeight: cameraLoading ? 200 : undefined,
      }}
    >
      {/* html5-qrcode fixes the <video> width in px from the reader's width at start, while its
          shading overlay stretches to the reader — so the reader must be full width from the
          start. The spinner is overlaid rather than laid out beside it for that reason. */}
      <div id={elementId} style={{ width: '100%' }} />
      {cameraLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};
